import React, { useCallback, useEffect, useState } from "react";
import * as Yup from "yup";
import { Formik, Form } from "formik";

import {
  Box,
  Button,
  Flex,
  Grid,
  Label,
  FileUpload,
  FormHeading,
  ErrorMsg,
  Toastify,
} from "atoms";
import { FormRadio } from "molecules/FormInput";
import { usePropsoch } from "contexts/Propsoch";
import { useParams } from "react-router-dom";

const validationSchema = Yup.object().shape({
  developmentModel: Yup.string()
    .required("Developmental model is required")
    .typeError("Developmental model is required"),
});
export const LegalForm = ({
  onNext,
  onPrevious,
  bindHitOnSave,
  fetchProperty,
  rootProperty,
}) => {
  const {
    actions: {
      getPropertyListingLegal,
      createPropertyListingLegal,
      updatePropertyListingLegal,
    },
  } = usePropsoch();

  const { id } = useParams();

  // fetch property
  const [property, setProperty] = useState(null);

  useEffect(() => {
    id &&
      getPropertyListingLegal(id)?.then((data) => {
        setProperty(data);

        // const compliances = [];
        // const compliancesData = {};

        // data?.compliances?.forEach((item) => {
        //   if (item?.value) {
        //     compliances.push(item?.type);
        //   }
        //   compliancesData[item?.type] = {};
        //   compliancesData[item?.type].images = item?.images;
        // });

        // reformat type
        data &&
          setProperty((prev) => ({
            ...prev,
            legalOpinionImages:
              data?.legal?.find((i) => i?.type === "legalOpinion")?.images ||
              [],
            developmentModel:
              data?.legal?.find((i) => i?.type === "developmentalModel")
                ?.value || "",
            developmentModelImages:
              data?.legal?.find((i) => i?.type === "developmentalModel")
                ?.images || [],
          }));
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // submit
  const [submitLoading, setSubmitLoading] = useState(false);
  const onSubmit = useCallback(
    ({
      legalOpinionImages,
      developmentModel,
      developmentModelImages,
      dirty,
    }) => {
      setSubmitLoading(true);
      if (dirty) {
        const formattedValues = {
          propertyId: id,
          legal: [
            { type: "legalOpinion", images: legalOpinionImages },
            {
              type: "developmentalModel",
              value: developmentModel,
              images: developmentModelImages,
            },
          ],
        };

        if (property) {
          updatePropertyListingLegal(
            { ...formattedValues },
            id,
            rootProperty?.name
          )
            .then(() => {
              Toastify("success", "Legal updated");
              fetchProperty()?.then(() => {
                onNext();
              });
            })
            .catch((e) => {
              Toastify("error", "Legal not updated");
            })
            .finally(() => {
              setSubmitLoading(false);
            });
        } else {
          createPropertyListingLegal(formattedValues, rootProperty?.name)
            .then(() => {
              Toastify("success", "Legal created");
              fetchProperty()?.then(() => {
                onNext();
              });
            })
            .catch((e) => {
              Toastify("error", "Legal not created");
            })
            .finally(() => {
              setSubmitLoading(false);
            });
        }
      } else {
        if (id) {
          onNext();
        }
      }
    },
    [
      createPropertyListingLegal,
      id,
      updatePropertyListingLegal,
      onNext,
      property,
      rootProperty,
      fetchProperty,
    ]
  );

  return (
    <Formik
      initialValues={
        property
          ? property
          : {
              legalOpinionImages: "",
              developmentModel: "",
              developmentModelImages: "",
            }
      }
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      enableReinitialize
    >
      {({ values, setFieldValue, submitForm, dirty, touched, errors }) => {
        bindHitOnSave(() => {
          setFieldValue("dirty", dirty);
          submitForm();
        });
        return (
          <Form>
            <FormHeading text="Legal" />
            <Grid gridGap="3.2rem" mt="2.4rem">
              <Box>
                <Label text="Legal Opinion" mb="2rem" />
                <FileUpload
                  files={values?.legalOpinionImages || []}
                  onUpload={(locations) => {
                    const newProjectImages = [
                      ...(values?.legalOpinionImages
                        ? JSON.parse(
                            JSON.stringify(values?.legalOpinionImages)
                          ) || []
                        : []),
                      ...locations?.map((i) => ({ image: i, alt: "" })),
                    ];

                    setFieldValue("legalOpinionImages", newProjectImages);
                  }}
                  onSubmitDescription={({ alt, file }) => {
                    setFieldValue(
                      `legalOpinionImages.[${file?.index}].alt`,
                      alt
                    );
                  }}
                  onCross={(index) => {
                    const currProjectImages = [
                      ...(values?.legalOpinionImages
                        ? JSON.parse(
                            JSON.stringify(values?.legalOpinionImages)
                          ) || []
                        : []),
                    ];
                    const total = currProjectImages?.length;

                    const newProjectImages = [
                      ...currProjectImages?.slice(0, index),
                      ...currProjectImages?.slice(index + 1, total),
                    ];

                    setFieldValue("legalOpinionImages", newProjectImages);
                  }}
                  inputFormat="application/pdf"
                  ext={["pdf"]}
                />
              </Box>

              <Box>
                <Label text="Developmental Model" req mb="2rem" />
                <Grid
                  gridAutoFlow="column"
                  gridGap="2.4rem"
                  justifyContent="start"
                  alignItems="center"
                  // gridGap="3rem"
                  mb="1.6rem"
                >
                  <FormRadio
                    name="developmentModel"
                    label="Joint Development"
                    value="jointDevelopment"
                  />
                  <FormRadio
                    name="developmentModel"
                    label="Company owned"
                    value="companyOwned"
                  />
                  <FormRadio
                    name="developmentModel"
                    label="Developer owned"
                    value="developerOwned"
                  />
                </Grid>
                {touched?.developmentModel && errors?.developmentModel && (
                  <ErrorMsg text={errors?.developmentModel} />
                )}
                <FileUpload
                  files={values?.developmentModelImages || []}
                  onUpload={(locations) => {
                    const newProjectImages = [
                      ...(values?.developmentModelImages
                        ? JSON.parse(
                            JSON.stringify(values?.developmentModelImages)
                          ) || []
                        : []),
                      ...locations?.map((i) => ({ image: i, alt: "" })),
                    ];

                    setFieldValue("developmentModelImages", newProjectImages);
                  }}
                  onSubmitDescription={({ alt, file }) => {
                    setFieldValue(
                      `developmentModelImages.[${file?.index}].alt`,
                      alt
                    );
                  }}
                  onCross={(index) => {
                    const currProjectImages = [
                      ...(values?.developmentModelImages
                        ? JSON.parse(
                            JSON.stringify(values?.developmentModelImages)
                          ) || []
                        : []),
                    ];
                    const total = currProjectImages?.length;

                    const newProjectImages = [
                      ...currProjectImages?.slice(0, index),
                      ...currProjectImages?.slice(index + 1, total),
                    ];

                    setFieldValue("developmentModelImages", newProjectImages);
                  }}
                  inputFormat="application/pdf"
                  ext={["pdf"]}
                />
              </Box>

              <Flex my="2rem" gridGap="1.6rem">
                <Button
                  px="3rem"
                  width="fit-content"
                  variant="primaryOutline"
                  height="4.8rem"
                  onClick={onPrevious}
                >
                  Previous
                </Button>
                <Button
                  px="3rem"
                  width="fit-content"
                  variant="primary"
                  height="4.8rem"
                  type="submit"
                  loading={submitLoading}
                  onClick={() => {
                    setFieldValue("dirty", dirty);
                    submitForm();
                  }}
                >
                  Next
                </Button>
              </Flex>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};
